import React, { useState } from 'react';
import { Box, Typography, Paper, Container, Tab, Tabs, SelectChangeEvent } from '@mui/material';
import { Button, TextField, Card, Checkbox, RadioGroup, Select } from '../ui';
import { Page, PageSection } from '../layout/index';
import { EBarChart, ELineChart, ERadarChart, WaterQualityGauge } from '../charts';
import { WaterQualityVisualization } from '../water-quality-visualization';
import { sampleWaterQuality, hardWaterSample, softWaterSample, roWaterSample } from '../../utils/mockData';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <Box
      role="tabpanel"
      hidden={value !== index}
      id={`ui-demo-tabpanel-${index}`}
      aria-labelledby={`ui-demo-tab-${index}`}
      sx={{ py: 3 }}
      {...other}
    >
      {value === index && children}
    </Box>
  );
}

export const UIComponentsDemo: React.FC = () => {
  const [tabValue, setTabValue] = useState(0);
  const [selectedWaterProfile, setSelectedWaterProfile] = useState(sampleWaterQuality);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  // Sample data for charts
  const barChartData = [
    { name: 'Jan', sales: 400, target: 300, profit: 240 },
    { name: 'Feb', sales: 300, target: 400, profit: 139 },
    { name: 'Mar', sales: 200, target: 300, profit: 90 },
    { name: 'Apr', sales: 278, target: 250, profit: 150 },
    { name: 'May', sales: 189, target: 200, profit: 80 },
    { name: 'Jun', sales: 239, target: 220, profit: 110 },
  ];

  const lineChartData = [
    { name: 'Week 1', temperature: 28, humidity: 60 },
    { name: 'Week 2', temperature: 30, humidity: 55 },
    { name: 'Week 3', temperature: 27, humidity: 70 },
    { name: 'Week 4', temperature: 29, humidity: 65 },
    { name: 'Week 5', temperature: 32, humidity: 50 },
  ];

  const radarChartData = [
    {
      attribute: 'Taste',
      'Product A': 80,
      'Product B': 90,
      'Product C': 70,
    },
    {
      attribute: 'Quality',
      'Product A': 90,
      'Product B': 85,
      'Product C': 95,
    },
    {
      attribute: 'Value',
      'Product A': 70,
      'Product B': 80,
      'Product C': 85,
    },
    {
      attribute: 'Design',
      'Product A': 85,
      'Product B': 75,
      'Product C': 80,
    },
    {
      attribute: 'Usability',
      'Product A': 75,
      'Product B': 90,
      'Product C': 85,
    },
  ];

  // Sample form options
  const selectOptions = [
    { value: 'option1', label: 'Option 1' },
    { value: 'option2', label: 'Option 2' },
    { value: 'option3', label: 'Option 3' },
  ];

  const radioOptions = [
    { value: 'radio1', label: 'Radio Option 1' },
    { value: 'radio2', label: 'Radio Option 2' },
    { value: 'radio3', label: 'Radio Option 3' },
  ];

  // Water profile selection options
  const waterProfiles = [
    { value: 'sample', label: 'Tap Water Sample' },
    { value: 'hard', label: 'Hard Well Water' },
    { value: 'soft', label: 'Rainwater' },
    { value: 'ro', label: 'Reverse Osmosis Water' },
  ];
  const handleWaterProfileChange = (event: SelectChangeEvent<string | number>) => {
    const value = event.target.value as string;
    
    switch (value) {
      case 'hard':
        setSelectedWaterProfile(hardWaterSample);
        break;
      case 'soft':
        setSelectedWaterProfile(softWaterSample);
        break;
      case 'ro':
        setSelectedWaterProfile(roWaterSample);
        break;
      default:
        setSelectedWaterProfile(sampleWaterQuality);
    }
  };

  return (
    <Page 
      title="UI Components Demo" 
      description="A demonstration of the FlahaFast UI component library and visualization components"
    >
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs value={tabValue} onChange={handleTabChange} aria-label="UI component demo tabs">
          <Tab label="Basic UI Components" />
          <Tab label="Charts & Visualizations" />
          <Tab label="Water Quality Visualization" />
        </Tabs>
      </Box>

      <TabPanel value={tabValue} index={0}>
        <PageSection title="Buttons">
          <Box sx={{ display: 'flex', flexWrap: 'wrap', margin: '-8px' }}>
            <Box sx={{ padding: 1, flexBasis: { xs: '100%', md: '50%' } }}>
              <Paper sx={{ p: 3 }}>
                <Typography variant="h6" gutterBottom>Button Variants</Typography>
                <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                  <Button variant="contained">Primary</Button>
                  <Button variant="contained" color="secondary">Secondary</Button>
                  <Button variant="outlined">Outlined</Button>
                  <Button variant="text">Text</Button>
                </Box>

                <Typography variant="h6" gutterBottom>Button Sizes</Typography>
                <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                  <Button size="small">Small</Button>
                  <Button size="medium">Medium</Button>
                  <Button size="large">Large</Button>
                </Box>
              </Paper>
            </Box>

            <Box sx={{ padding: 1, flexBasis: { xs: '100%', md: '50%' } }}>
              <Paper sx={{ p: 3 }}>
                <Typography variant="h6" gutterBottom>Button States</Typography>
                <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                  <Button variant="contained" color="success">Success</Button>
                  <Button variant="contained" color="error">Error</Button>
                  <Button variant="contained" color="warning">Warning</Button>
                  <Button variant="contained" color="info">Info</Button>
                </Box>

                <Typography variant="h6" gutterBottom>Button with Icon</Typography>
                <Box sx={{ display: 'flex', gap: 2 }}>
                  <Button variant="contained" startIcon={<span>+</span>}>Add Item</Button>
                  <Button variant="outlined" endIcon={<span>→</span>}>Next</Button>
                </Box>
              </Paper>
            </Box>
          </Box>
        </PageSection>

        <PageSection title="Form Components" divider>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', margin: '-12px' }}>
            <Box sx={{ padding: 1.5, flexBasis: { xs: '100%', md: '50%' } }}>
              <Card title="Text Fields">
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <TextField label="Standard" />
                  <TextField label="With Helper Text" helperText="Some important helper text" />
                  <TextField label="With Error" error errorText="This field is required" />
                  <TextField label="With Start Adornment" startAdornment="$" />
                  <TextField label="With End Adornment" endAdornment="kg" />
                </Box>
              </Card>
            </Box>

            <Box sx={{ padding: 1.5, flexBasis: { xs: '100%', md: '50%' } }}>
              <Card title="Selection & Toggle Components">
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                  <Select
                    label="Select Option"
                    options={selectOptions}
                    helperText="Choose from the list"
                  />

                  <RadioGroup
                    label="Radio Group"
                    options={radioOptions}
                    row
                  />

                  <Checkbox
                    label="Agree to terms and conditions"
                  />

                  <Checkbox
                    label="Subscribe to newsletter"
                    checked
                  />
                </Box>
              </Card>
            </Box>
          </Box>
        </PageSection>

        <PageSection title="Cards" divider>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', margin: '-12px' }}>
            <Box sx={{ padding: 1.5, flexBasis: { xs: '100%', sm: '50%', md: '33.33%' } }}>
              <Card
                title="Basic Card"
                subtitle="With title and subtitle"
              >
                <Typography variant="body2">
                  This is a basic card with title and subtitle. Cards can contain various content types.
                </Typography>
              </Card>
            </Box>

            <Box sx={{ padding: 1.5, flexBasis: { xs: '100%', sm: '50%', md: '33.33%' } }}>
              <Card
                title="Card with Actions"
                actions={
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button size="small">Cancel</Button>
                    <Button size="small" variant="contained">Submit</Button>
                  </Box>
                }
              >
                <Typography variant="body2">
                  This card includes action buttons at the bottom, useful for forms or interactive content.
                </Typography>
              </Card>
            </Box>

            <Box sx={{ padding: 1.5, flexBasis: { xs: '100%', sm: '50%', md: '33.33%' } }}>
              <Card
                title="Elevated Card"
                elevation={6}
              >
                <Typography variant="body2">
                  This card has increased elevation for emphasis. Use higher elevation to create visual hierarchy.
                </Typography>
              </Card>
            </Box>
          </Box>
        </PageSection>
      </TabPanel>

      <TabPanel value={tabValue} index={1}>
        <PageSection title="Charts">
          <Box sx={{ display: 'flex', flexWrap: 'wrap', margin: '-12px' }}>
            <Box sx={{ padding: 1.5, flexBasis: { xs: '100%', md: '50%' } }}>              <Card title="Bar Chart">
                <EBarChart
                  data={barChartData}
                  series={[
                    { dataKey: 'sales', color: '#2196f3' },
                    { dataKey: 'target', color: '#f44336' },
                    { dataKey: 'profit', color: '#4caf50' }
                  ]}
                  height={300}
                  grid={true}
                />
              </Card>
            </Box>
            
            <Box sx={{ padding: 1.5, flexBasis: { xs: '100%', md: '50%' } }}>              <Card title="Line Chart">
                <ELineChart
                  data={lineChartData}
                  series={[
                    { dataKey: 'temperature', color: '#ff9800' },
                    { dataKey: 'humidity', color: '#2196f3' }
                  ]}
                  height={300}
                  grid={true}
                  zoom={true}
                />
              </Card>
            </Box>

            <Box sx={{ padding: 1.5, flexBasis: { xs: '100%', md: '50%' } }}>              <Card title="Radar Chart">
                <ERadarChart
                  data={radarChartData}
                  series={[
                    { dataKey: 'Product A', color: '#2196f3' },
                    { dataKey: 'Product B', color: '#f44336' },
                    { dataKey: 'Product C', color: '#4caf50' }
                  ]}
                  height={300}
                />
              </Card>
            </Box>
            
            <Box sx={{ padding: 1.5, flexBasis: { xs: '100%', md: '50%' } }}>
              <Card title="Water Quality Gauge">
                <Box sx={{ display: 'flex', justifyContent: 'center' }}>                  <WaterQualityGauge
                    title="pH Level"
                    value={6.2}
                    min={0}
                    max={14}
                    optimalStart={5.5}
                    optimalEnd={6.5}
                    colors={{
                      belowOptimal: '#f39c12',
                      optimal: '#2ecc71',
                      aboveOptimal: '#e74c3c',
                    }}
                  />
                </Box>
              </Card>
            </Box>
          </Box>
        </PageSection>
      </TabPanel>

      <TabPanel value={tabValue} index={2}>
        <PageSection title="Water Profile Selection">
          <Box sx={{ maxWidth: 500, mb: 4 }}>
            <Select
              label="Select Water Profile"
              options={waterProfiles}
              value="sample"
              onChange={handleWaterProfileChange}
              helperText="Choose a water profile to visualize"
            />
          </Box>
        </PageSection>

        <PageSection title="Water Quality Visualization">
          <WaterQualityVisualization waterData={selectedWaterProfile} />
        </PageSection>
      </TabPanel>
    </Page>
  );
};

export default UIComponentsDemo;
